// Э-11. Администрирование: проекты и корпуса (мастер создания, изменение), участники, пороги, пользователи,
// роли с правами, справочники (виды работ, бригады, причины простоя, категории проблем).
// Замечания владельца 24.10.2026: роль — одна на всю систему; проекты и корпуса меняются; новый корпус —
// с пустыми полями и несколькими шаблонами этажей; свои виды работ; пароль можно показать.

import { useState } from "react";
import { setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Drawer, Empty, Field, Input, Loading, Modal, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { api, can, useCtx } from "../ctx";
import { fmtDate } from "../../shared/format";
import { DeleteRequestModal, PendingPanel, StateDrawer, StateTag } from "../lifecycle";
import { BrigadesTab } from "./Brigades";
import { FloorsDrawer } from "./Floors";

type Tab = "projects" | "users" | "roles" | "catalogs";
const WEEK = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

export function AdminPage() {
  const { me } = useCtx();
  const r = useRoute();
  const items: { value: Tab; label: string }[] = [];
  if (can(me, "admin.projects")) items.push({ value: "projects", label: "Проекты и корпуса" });
  if (can(me, "admin.users")) items.push({ value: "users", label: "Пользователи" }, { value: "roles", label: "Роли и права" });
  if (can(me, "admin.catalogs")) items.push({ value: "catalogs", label: "Справочники" });
  const asked = r.query.get("tab") as Tab | null;
  const tab = items.find((x) => x.value === asked)?.value || items[0]?.value;
  return (
    <>
      <TopBar title="Администрирование" />
      <div className="content">
        {items.length > 1 && <Tabs<Tab> value={tab!} onChange={(v) => setQuery({ tab: v === items[0].value ? null : v })} items={items} />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "roles" && <RolesTab />}
        {tab === "catalogs" && <CatalogsTab />}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ проекты

function ProjectsTab() {
  const toast = useToast();
  const p = useAsync<any[]>(() => api.get("/projects"), []);
  const users = useAsync<any[]>(() => api.get("/users"), []);
  const [project, setProject] = useState<any | null>(null); // {} — новый, иначе изменить
  const [wizard, setWizard] = useState<any | null>(null);
  const [members, setMembers] = useState<any | null>(null);
  const [settings, setSettings] = useState<any | null>(null);
  const [calendar, setCalendar] = useState<any | null>(null);
  const [volumes, setVolumes] = useState<any | null>(null);
  const [replan, setReplan] = useState<any | null>(null);
  const [edit, setEdit] = useState<any | null>(null);
  const [floorsOf, setFloorsOf] = useState<any | null>(null);
  const [stateOf, setStateOf] = useState<{ kind: "project" | "building"; item: any } | null>(null);
  const [deleteOf, setDeleteOf] = useState<{ kind: "project" | "building"; item: any } | null>(null);
  const [unapprove, setUnapprove] = useState<any | null>(null);
  const [brigadesOf, setBrigadesOf] = useState<any | null>(null);
  const [pendingKey, setPendingKey] = useState(0);
  const { me } = useCtx();
  const approver = can(me, "projects.approve");
  if (!p.data) return <Loading error={p.error} onRetry={p.reload} />;
  const changed = () => { p.reload(); setPendingKey((k) => k + 1); };
  return (
    <>
      <PendingPanel key={pendingKey} onChanged={p.reload} />
      <div><Button variant="primary" onClick={() => setProject({})}>Новый проект</Button></div>
      {p.data.length === 0 && <Empty>Проектов нет.</Empty>}
      {p.data.map((proj) => (
        <div key={proj.id} className="panel">
          <div className="ph">
            <div><h2>{proj.name} <StateTag state={proj.state} /></h2><div className="muted small">{[proj.address, proj.legal_entity && `${proj.legal_entity}${proj.legal_role_label ? ` — ${proj.legal_role_label}` : ""}`].filter(Boolean).join("; ") || "адрес не указан"}</div></div>
            <div className="row">
              <Button size="small" onClick={() => setProject(proj)}>Изменить</Button>
              <Button size="small" onClick={() => setMembers(proj)}>Участники</Button>
              <Button size="small" onClick={() => setSettings(proj)}>Пороги</Button>
              <Button size="small" onClick={() => setBrigadesOf(proj)}>Бригады</Button>
              <Button size="small" onClick={() => setStateOf({ kind: "project", item: proj })}>Состояние</Button>
              <Button size="small" variant="ghost" onClick={() => setDeleteOf({ kind: "project", item: proj })}>Удалить</Button>
              <Button size="small" variant="primary" onClick={() => setWizard(proj)}>Новый корпус</Button>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Корпус</th><th className="num">Этажей</th><th>Старт</th><th className="num">Готово циклов</th><th>Согласование</th><th>Действия</th></tr></thead>
              <tbody>
                {proj.buildings.length === 0 && <tr><td colSpan={6} className="muted">Корпусов нет</td></tr>}
                {proj.buildings.map((b: any) => (
                  <tr key={b.id}>
                    <td><b>{b.name}</b> <StateTag state={b.state} /></td><td className="num">{b.floors_count}</td><td>{fmtDate(b.start_date)}</td><td className="num">{b.cycles_done} / {b.cycles_total}</td>
                    <td className="small">{b.approved ? <>согласован{b.approved_by ? `: ${b.approved_by}` : ""}{approver && <> · <button type="button" className="link" onClick={() => setUnapprove(b)}>снять</button></>}</> : <span className="tag s-loading">ждёт согласования</span>}</td>
                    <td className="row tight">
                      <Button size="small" onClick={() => setEdit({ ...b, project_id: proj.id })}>Изменить</Button>
                      <Button size="small" onClick={() => setCalendar(b)}>Календарь</Button>
                      <Button size="small" onClick={() => setVolumes(b)}>Объёмы</Button>
                      <Button size="small" onClick={() => setReplan(b)}>Перестроить план</Button>
                      <Button size="small" onClick={() => setFloorsOf({ ...b, project_id: proj.id })}>Этажи</Button>
                      <Button size="small" onClick={() => setStateOf({ kind: "building", item: b })}>Состояние</Button>
                      <Button size="small" variant="ghost" onClick={() => setDeleteOf({ kind: "building", item: b })}>Удалить</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {project && <ProjectDrawer item={project} users={users.data || []} onClose={() => setProject(null)} onDone={() => { setProject(null); p.reload(); }} />}
      {wizard && <BuildingWizard project={wizard} onClose={() => setWizard(null)} onDone={() => { setWizard(null); p.reload(); }} />}
      {members && <MembersDrawer project={members} users={users.data || []} onClose={() => setMembers(null)} />}
      {settings && <SettingsDrawer project={settings} onClose={() => setSettings(null)} onDone={() => { setSettings(null); p.reload(); }} />}
      {replan && (
        <Modal title={`Перестроить план: ${replan.name}`} onClose={() => setReplan(null)} actions={<>
          <Button onClick={() => setReplan(null)}>Отмена</Button>
          <Button variant="primary" onClick={async () => {
            try {
              const r = await api.post(`/buildings/${replan.id}/generate-plan`, { reason: "Изменён шаблон цикла" });
              toast.ok(r.changed ? `План перестроен с ${r.from_cycle}-го цикла по текущему шаблону` : "Не начатых этажей нет — перестраивать нечего");
              setReplan(null); p.reload();
            } catch (e) { toast.error(e); }
          }}>Перестроить</Button>
        </>}>
          <p>Этажи, на которых работы ещё не начаты, получат даты, бригады и работы по текущему шаблону цикла. Начатые и законченные этажи не меняются. Прежняя версия плана сохранится в «Истории».</p>
        </Modal>
      )}
      {volumes && <VolumesDrawer building={volumes} onClose={() => setVolumes(null)} />}
      {floorsOf && <FloorsDrawer building={floorsOf} onClose={() => setFloorsOf(null)} onChanged={() => p.reload()} />}
      {edit && <BuildingEditDrawer building={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); p.reload(); }} />}
      {brigadesOf && (
        <Drawer title={`Бригады: ${brigadesOf.name}`} onClose={() => setBrigadesOf(null)} footer={<Button onClick={() => setBrigadesOf(null)}>Готово</Button>}>
          <BrigadesTab projectId={brigadesOf.id} canEdit />
        </Drawer>
      )}
      {stateOf && <StateDrawer kind={stateOf.kind} item={stateOf.item} onClose={() => setStateOf(null)} onDone={() => { setStateOf(null); changed(); }} />}
      {deleteOf && <DeleteRequestModal kind={deleteOf.kind} item={deleteOf.item} onClose={() => setDeleteOf(null)} onDone={() => { setDeleteOf(null); changed(); }} />}
      {unapprove && <UnapproveModal b={unapprove} onClose={() => setUnapprove(null)} onDone={() => { setUnapprove(null); changed(); }} />}
      {calendar && <CalendarDrawer building={calendar} onClose={() => setCalendar(null)} onDone={() => { setCalendar(null); p.reload(); }} />}
    </>
  );
}

/** Снять согласование, чтобы исправить ошибку в этажах, шаблонах или дате старта. */
function UnapproveModal({ b, onClose, onDone }: { b: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Снять согласование: ${b.name}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={reason.trim().length < 3} onClick={async () => {
        try { await api.post(`/buildings/${b.id}/unapprove`, { reason: reason.trim() }); toast.ok("Согласование снято — администратор может править этажи и шаблоны"); onDone(); } catch (e) { toast.error(e); }
      }}>Снять согласование</Button>
    </>}>
      <Field label="Что нужно исправить"><Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Например: не тот шаблон у этажей 11–18" /></Field>
      <p className="small muted">После правок корпус снова попадёт в «Требует решения» на согласование.</p>
    </Modal>
  );
}

/** Новый проект ({}) или изменение существующего: название, адрес, юрлицо; участники — сразу при создании. */
function ProjectDrawer({ item, users, onClose, onDone }: { item: any; users: any[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const isNew = !item.id;
  const ents = useAsync<any[]>(() => api.get("/legal-entities"), []);
  const [v, setV] = useState({ name: item.name || "", address: item.address || "", legal_entity_id: item.legal_entity_id || "", legal_role: item.legal_role || "" });
  const [members, setMembers] = useState<string[]>([]);
  const [newEnt, setNewEnt] = useState<any | null>(null);
  const changed = isNew || v.name !== item.name || v.address !== (item.address || "") || v.legal_entity_id !== (item.legal_entity_id || "") || v.legal_role !== (item.legal_role || "");
  const save = async () => {
    const body = { name: v.name.trim(), address: v.address.trim() || null, legal_entity_id: v.legal_entity_id || null, legal_role: v.legal_entity_id ? v.legal_role || null : null };
    try {
      if (isNew) await api.post("/projects", { ...body, member_ids: members }); else await api.patch(`/projects/${item.id}`, body);
      toast.ok(isNew ? (members.length ? `Проект создан, участников: ${members.length}` : "Проект создан") : "Проект сохранён");
      onDone();
    } catch (e) { toast.error(e); }
  };
  const candidates = users.filter((u) => u.is_active && !u.is_admin);
  return (
    <Drawer narrow title={isNew ? "Новый проект" : `Проект: ${item.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!v.name.trim() || !changed} onClick={save}>{isNew ? "Создать" : "Сохранить"}</Button>
    </>}>
      <div className="form-grid">
        <Field label="Название" wide><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Например: ЖК «Северный»" /></Field>
        <Field label="Адрес" wide><Input value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} placeholder="Город, улица, дом" /></Field>
        <Field label="Юрлицо" wide hint="Наше юрлицо, которое ведёт работы; справочник — «Справочники» → «Юрлица»">
          <div className="row tight" style={{ flexWrap: "nowrap" }}>
            <Select className="grow" value={v.legal_entity_id} placeholder="Не выбрано" onChange={(e) => setV({ ...v, legal_entity_id: e.target.value })}
              options={(ents.data || []).map((x) => ({ value: x.id, label: x.inn ? `${x.name} · ИНН ${x.inn}` : x.name }))} />
            <Button size="small" onClick={() => setNewEnt({})}>Новое</Button>
          </div>
        </Field>
        {v.legal_entity_id && (
          <Field label="Кто мы в проекте" wide><Select value={v.legal_role} placeholder="Выберите" onChange={(e) => setV({ ...v, legal_role: e.target.value })} options={Object.entries(LEGAL_ROLE).map(([value, label]) => ({ value, label }))} /></Field>
        )}
        {isNew && (
          <Field label="Участники" wide hint="Кто будет работать в проекте; позже — кнопка «Участники». Администратор видит все проекты и так">
            <div className="stack tight">{candidates.length === 0 ? <span className="muted small">Пользователей пока нет — заведите их на вкладке «Пользователи».</span> : candidates.map((u) => (
              <label key={u.id} className="check"><input type="checkbox" checked={members.includes(u.id)} onChange={(e) => setMembers(e.target.checked ? [...members, u.id] : members.filter((x) => x !== u.id))} />
                {u.full_name}<span className="muted small">{u.role ? ` — ${u.role.toLowerCase()}` : ""}</span></label>
            ))}</div>
          </Field>
        )}
      </div>
      {newEnt && <LegalEntityDrawer e={newEnt} onClose={() => setNewEnt(null)} onDone={(x) => { setNewEnt(null); ents.reload(); if (x) setV((cur) => ({ ...cur, legal_entity_id: x.id })); }} />}
    </Drawer>
  );
}

type Range = { from: string; to: string; kind: string; template_id: string };

/** Мастер нового корпуса (встречи 24.10.2026). Поля пустые — администратор заполняет сам.
 * Корпус идёт этапами: подготовительный → фундаментная плита → подземные этажи → надземные этажи.
 * Подземные и надземные этажи — «типовые» (один шаблон) или со своим шаблоном у этажа / диапазона этажей.
 * Объёмы бетона и арматуры не вводятся — считаются по шаблонам этажей. */
function BuildingWizard({ project, onClose, onDone }: { project: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const tpl = useAsync<any[]>(() => api.get("/cycle-templates", { project_id: project.id }), [project.id]);
  const [v, setV] = useState({
    name: "", floors_count: "", start_date: "", shifts_per_day: "", workdays: [] as number[],
    winter_from: "", winter_to: "", external_site_id: "",
    prep: true, prep_template_id: "", foundation: true, foundation_template_id: "",
    underground: "0", under_typical: true, under_template_id: "", under_own: {} as Record<number, string>,
    above_typical: true, above_template_id: "",
  });
  const [ranges, setRanges] = useState<Range[]>([]);
  const set = (p: Partial<typeof v>) => setV({ ...v, ...p });
  const all = tpl.data || [];
  const of = (stage: string) => all.filter((t) => (t.stage || "floor") === stage);
  const opts = (stage: string) => of(stage).map((t) => ({ value: t.id, label: `${t.name} — ${t.step_days} дн.${t.problems.length ? " (есть ошибки)" : ""}` }));
  const byId = (id: string) => all.find((t) => t.id === id);
  // этап включён по умолчанию, если для него есть шаблон; шаблон — первый подходящий
  const prepOn = v.prep && of("prep").length > 0, foundOn = v.foundation && of("foundation").length > 0;
  const prepTpl = v.prep_template_id || of("prep")[0]?.id || "", foundTpl = v.foundation_template_id || of("foundation")[0]?.id || "";
  const floors = Number(v.floors_count) || 0;
  const under = Number(v.underground) || 0;
  const aboveFor = (n: number): { kind: string; template_id: string } | null => {
    if (v.above_typical) return v.above_template_id ? { kind: "typical", template_id: v.above_template_id } : null;
    const r = ranges.find((x) => Number(x.from) <= n && n <= Number(x.to));
    return r && r.template_id ? { kind: r.kind, template_id: r.template_id } : null;
  };
  const underFor = (n: number) => (v.under_typical ? v.under_template_id : v.under_own[n] || "");
  // проверка диапазонов: каждый надземный этаж — ровно в одном диапазоне
  const rangeError = (() => {
    if (v.above_typical || !floors) return null;
    for (const r of ranges) {
      const a = Number(r.from), b = Number(r.to);
      if (!Number.isInteger(a) || !Number.isInteger(b) || r.from === "" || r.to === "") return "У каждого диапазона укажите этажи «с» и «по»";
      if (a < 1 || b > floors || a > b) return `Диапазон ${r.from}–${r.to}: этажи от 1 до ${floors}, «с» не больше «по»`;
    }
    for (let n = 1; n <= floors; n++) {
      const k = ranges.filter((x) => Number(x.from) <= n && n <= Number(x.to)).length;
      if (k === 0) return `Этаж ${n} не попал ни в один диапазон`;
      if (k > 1) return `Этаж ${n} попал в два диапазона`;
    }
    return null;
  })();
  const missing = [
    !v.name.trim() && "название", !v.start_date && "дату старта работ", !v.shifts_per_day && "смен в сутки", !v.workdays.length && "рабочие дни",
    !(floors >= 1) && "число надземных этажей",
    v.above_typical && !v.above_template_id && "шаблон надземных этажей",
    !v.above_typical && ranges.some((r) => !r.template_id) && "шаблон у каждого диапазона",
    under > 0 && v.under_typical && !v.under_template_id && "шаблон подземных этажей",
    under > 0 && !v.under_typical && Array.from({ length: under }, (_, i) => -(i + 1)).some((n) => !v.under_own[n]) && "шаблон каждого подземного этажа",
    prepOn && !prepTpl && "шаблон подготовительного этапа", foundOn && !foundTpl && "шаблон фундаментной плиты",
  ].filter(Boolean) as string[];
  // последовательность для оценки срока: этапы, подземные (снизу вверх), надземные
  const seq: (any | undefined)[] = [
    ...(prepOn ? [byId(prepTpl)] : []), ...(foundOn ? [byId(foundTpl)] : []),
    ...Array.from({ length: under }, (_, i) => byId(underFor(-(under - i)))),
    ...Array.from({ length: floors }, (_, i) => byId(aboveFor(i + 1)?.template_id || "")),
  ];
  const known = seq.length > 0 && seq.every(Boolean);
  const days = known ? seq.slice(0, -1).reduce((s, t) => s + t.step_days, 0) + seq[seq.length - 1].span_days : 0;
  const weeks = known && v.workdays.length ? Math.round(days / v.workdays.length) : null;
  const mainTpl = byId(v.above_typical ? v.above_template_id : ranges[0]?.template_id || "");
  const save = async () => {
    const def = aboveFor(1)!.template_id;
    const list = [
      ...(prepOn ? [{ number: -100, kind: "prep", template_id: prepTpl }] : []),
      ...(foundOn ? [{ number: -99, kind: "foundation", template_id: foundTpl }] : []),
      ...Array.from({ length: under }, (_, i) => -(under - i)).map((n) => ({ number: n, kind: "underground", template_id: underFor(n) })),
      ...Array.from({ length: floors }, (_, i) => i + 1).map((n) => { const a = aboveFor(n)!; return { number: n, kind: a.kind, template_id: a.template_id === def ? null : a.template_id }; }),
    ];
    try {
      await api.post(`/projects/${project.id}/buildings`, {
        name: v.name.trim(), floors_count: floors, default_template_id: def, start_date: v.start_date,
        shifts_per_day: Number(v.shifts_per_day), workdays: v.workdays,
        winter_from: v.winter_from || null, winter_to: v.winter_to || null, external_site_id: v.external_site_id.trim() || null,
        underground_floors: under, floors: list,
      });
      toast.ok("Корпус создан, план построен по шаблонам");
      onDone();
    } catch (e) { toast.error(e); }
  };
  const kindOpts = [{ value: "first", label: "первый" }, { value: "typical", label: "типовой" }, { value: "tech", label: "технический" }, { value: "roof", label: "кровля, плита покрытия" }];
  const noTpl = (what: string) => <span className="small muted">Шаблона нет — создайте: «Шаблоны циклов» → «Новый шаблон» → «Для чего: {what}».</span>;
  return (
    <Drawer title={`Новый корпус: ${project.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={missing.length > 0 || !!rangeError} onClick={save}>Создать и построить план</Button>
    </>}>
      {!tpl.data ? <Loading /> : of("floor").length === 0 ? <Empty>Сначала создайте шаблон этажа на экране «Шаблоны циклов».</Empty> : (
        <div className="stack">
          <div className="form-grid">
            <Field label="Название"><Input value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="Например: Корпус 1" /></Field>
            <Field label="Старт работ" hint="С первого этапа. Можно в прошлом — если работы уже идут; сделанное внесите в «Этажи» → «Внести сделанное»"><Input type="date" value={v.start_date} onChange={(e) => set({ start_date: e.target.value })} /></Field>
            <Field label="Смен в сутки" hint="Ночная смена — если рядом нет жилых домов"><Select value={v.shifts_per_day} placeholder="Выберите" onChange={(e) => set({ shifts_per_day: e.target.value })} options={[{ value: "1", label: "1 — только день" }, { value: "2", label: "2 — день и ночь" }]} /></Field>
            <Field label="Рабочие дни" wide>
              <div className="row tight">{WEEK.map((d, i) => (
                <label key={d} className="check"><input type="checkbox" checked={v.workdays.includes(i)} onChange={(e) => set({ workdays: e.target.checked ? [...v.workdays, i].sort() : v.workdays.filter((x) => x !== i) })} />{d}</label>
              ))}</div>
            </Field>
          </div>

          <div className="panel pad stack tight">
            <h3>1. Этапы до этажей</h3>
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <label className="check" style={{ width: 230 }}><input type="checkbox" checked={prepOn} disabled={!of("prep").length} onChange={(e) => set({ prep: e.target.checked })} /> Подготовительный этап</label>
              {of("prep").length ? <Select aria-label="Шаблон подготовительного этапа" className="grow" value={prepTpl} disabled={!prepOn} onChange={(e) => set({ prep_template_id: e.target.value })} options={opts("prep")} /> : noTpl("подготовительный этап")}
            </div>
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <label className="check" style={{ width: 230 }}><input type="checkbox" checked={foundOn} disabled={!of("foundation").length} onChange={(e) => set({ foundation: e.target.checked })} /> Фундаментная плита</label>
              {of("foundation").length ? <Select aria-label="Шаблон фундаментной плиты" className="grow" value={foundTpl} disabled={!foundOn} onChange={(e) => set({ foundation_template_id: e.target.value })} options={opts("foundation")} /> : noTpl("фундаментная плита")}
            </div>
            <span className="small muted">Подготовительный — котлован, подбетонка, гидроизоляция, стяжка. Снимите галочку, если этап делают не ваши люди или он уже закончен до системы.</span>
          </div>

          <div className="panel pad stack tight">
            <h3>2. Подземные этажи</h3>
            <div className="row">
              <Field label="Сколько"><Select value={v.underground} style={{ width: 90 }} onChange={(e) => set({ underground: e.target.value })} options={["0", "1", "2", "3", "4", "5"].map((x) => ({ value: x, label: x }))} /></Field>
              {under > 0 && <label className="check"><input type="checkbox" checked={v.under_typical} onChange={(e) => set({ under_typical: e.target.checked })} /> Подземные этажи типовые — один шаблон</label>}
            </div>
            {under > 0 && (v.under_typical ? (
              <Select aria-label="Шаблон подземных этажей" value={v.under_template_id} placeholder="Выберите шаблон" onChange={(e) => set({ under_template_id: e.target.value })} options={opts("floor")} />
            ) : Array.from({ length: under }, (_, i) => -(i + 1)).map((n) => (
              <div key={n} className="row" style={{ flexWrap: "nowrap" }}>
                <span style={{ width: 90 }}>Этаж {n}</span>
                <Select aria-label={`Шаблон этажа ${n}`} className="grow" value={v.under_own[n] || ""} placeholder="Выберите шаблон" onChange={(e) => set({ under_own: { ...v.under_own, [n]: e.target.value } })} options={opts("floor")} />
              </div>
            )))}
            <span className="small muted">Нумерация: −1, −2… (ниже отметки 0 — пола первого этажа). Технический подземный этаж обычно нетиповой — снимите галочку и дайте ему свой шаблон.</span>
          </div>

          <div className="panel pad stack tight">
            <h3>3. Надземные этажи</h3>
            <div className="row">
              <Field label="Сколько" hint="С 1-го"><Input inputMode="numeric" style={{ width: 90 }} value={v.floors_count} onChange={(e) => set({ floors_count: e.target.value.replace(/\D/g, "") })} /></Field>
              <label className="check"><input type="checkbox" checked={v.above_typical} onChange={(e) => {
                const on = e.target.checked;
                if (!on && !ranges.length) setRanges([{ from: "1", to: String(floors || ""), kind: "typical", template_id: v.above_template_id }]);
                set({ above_typical: on });
              }} /> Все этажи типовые — один шаблон</label>
            </div>
            {v.above_typical ? (
              <Select aria-label="Шаблон надземных этажей" value={v.above_template_id} placeholder="Выберите шаблон" onChange={(e) => set({ above_template_id: e.target.value })} options={opts("floor")} />
            ) : (
              <>
                {ranges.map((x, i) => {
                  const ch = (p: Partial<Range>) => setRanges(ranges.map((y, k) => (k === i ? { ...y, ...p } : y)));
                  return (
                    <div key={i} className="row tight" style={{ flexWrap: "nowrap" }}>
                      <span className="small">с</span><Input aria-label="С этажа" inputMode="numeric" style={{ width: 60 }} value={x.from} onChange={(e) => ch({ from: e.target.value.replace(/\D/g, "") })} />
                      <span className="small">по</span><Input aria-label="По этаж" inputMode="numeric" style={{ width: 60 }} value={x.to} onChange={(e) => ch({ to: e.target.value.replace(/\D/g, "") })} />
                      <Select aria-label="Тип этажей" style={{ width: 150 }} value={x.kind} onChange={(e) => ch({ kind: e.target.value })} options={kindOpts} />
                      <Select aria-label="Шаблон диапазона" className="grow" value={x.template_id} placeholder="Выберите шаблон" onChange={(e) => ch({ template_id: e.target.value })} options={opts("floor")} />
                      <Button size="small" variant="ghost" onClick={() => setRanges(ranges.filter((_, k) => k !== i))}>Убрать</Button>
                    </div>
                  );
                })}
                <div><Button size="small" onClick={() => {
                  const last = ranges[ranges.length - 1];
                  const from = last && last.to ? Number(last.to) + 1 : 1;
                  setRanges([...ranges, { from: String(from), to: floors ? String(floors) : "", kind: "typical", template_id: "" }]);
                }}>+ диапазон этажей</Button></div>
                {rangeError && <div className="alert risk"><div className="t">{rangeError}</div></div>}
              </>
            )}
            <span className="small muted">Например: 1 — «первый» (магазины), 2–10 — типовой с более толстой арматурой, 11–18 — второй типовой. Шаблон для каждого типа этажа заводится на «Шаблонах циклов» (удобно — копией типового).</span>
          </div>

          <div className="form-grid">
            <Field label="Зимний режим с" hint="Необязательно; выдержки набора прочности увеличатся"><Input type="date" value={v.winter_from} onChange={(e) => set({ winter_from: e.target.value })} /></Field>
            <Field label="по"><Input type="date" value={v.winter_to} onChange={(e) => set({ winter_to: e.target.value })} /></Field>
            <Field label="Код объекта в «Бетоне»" hint="Необязательно: чтобы связать заказы бетона с этажами (этап 2)" wide><Input value={v.external_site_id} onChange={(e) => set({ external_site_id: e.target.value })} /></Field>
          </div>
          {mainTpl && Object.keys(mainTpl.volumes || {}).length > 0 && (
            <div className="small muted">Объёмы этажа — по шаблону «{mainTpl.name}»: {[
              mainTpl.volumes.concrete_m3 != null && `бетон ${fmtVol(mainTpl.volumes.concrete_m3)} м³`, mainTpl.volumes.rebar_t != null && `арматура ${fmtVol(mainTpl.volumes.rebar_t)} т`,
              mainTpl.volumes.formwork_m2 != null && `опалубка ${fmtVol(mainTpl.volumes.formwork_m2)} м²`,
            ].filter(Boolean).join(", ")}. Уточнить по этажам — кнопка «Объёмы» у корпуса.</div>
          )}
          {missing.length > 0 && <div className="small muted">Чтобы создать корпус, укажите: {missing.join(", ")}.</div>}
          {weeks != null && missing.length === 0 && !rangeError && (
            <div className="alert info"><div className="t">Примерно {weeks} недель: {seq.length} {seq.length === 1 ? "этап" : "этапов и этажей"}, около {days} рабочих дней.</div></div>
          )}
        </div>
      )}
    </Drawer>
  );
}

const fmtVol = (x: number) => String(Math.round(x * 10) / 10).replace(".", ",");

/** Изменение корпуса (замечание 24.10.2026, №3): название, шаблон типового этажа, старт, смены, код в «Бетоне».
 * Календарь, этажи и объёмы — своими кнопками. */
function BuildingEditDrawer({ building, onClose, onDone }: { building: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const d = useAsync<any>(() => api.get(`/buildings/${building.id}`), [building.id]);
  const tpl = useAsync<any[]>(() => api.get("/cycle-templates", { project_id: building.project_id }), [building.id]);
  const [v, setV] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const cur = v ?? (d.data ? {
    name: d.data.name, default_template_id: d.data.default_template_id || "", start_date: d.data.start_date || "",
    shifts_per_day: String(d.data.shifts_per_day), external_site_id: d.data.external_site_id || "",
  } : null);
  const set = (patch: any) => setV({ ...cur, ...patch });
  const started = !!d.data?.started;
  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, any> = { name: cur.name.trim(), shifts_per_day: Number(cur.shifts_per_day), external_site_id: cur.external_site_id.trim() || null };
      if (cur.default_template_id !== (d.data.default_template_id || "")) body.default_template_id = cur.default_template_id;
      if (cur.start_date !== d.data.start_date) body.start_date = cur.start_date;
      await api.patch(`/buildings/${building.id}`, body);
      toast.ok(body.default_template_id || body.start_date ? "Корпус сохранён, план не начатых этажей перестроен" : "Корпус сохранён");
      onDone();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <Drawer narrow title={`Корпус: ${building.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!cur || busy || !cur.name.trim() || !cur.default_template_id || !cur.start_date} onClick={save}>Сохранить</Button>
    </>}>
      {!cur || !tpl.data ? <Loading error={d.error || tpl.error} onRetry={d.reload} /> : (
        <div className="stack">
          <Field label="Название"><Input value={cur.name} onChange={(e) => set({ name: e.target.value })} /></Field>
          <Field label="Шаблон надземных этажей" hint="Этажи без своего шаблона, на которых работы ещё не начаты, перестроятся по новому шаблону; свой шаблон у этажа — в «Этажи»">
            <Select value={cur.default_template_id} placeholder="Выберите шаблон" onChange={(e) => set({ default_template_id: e.target.value })}
              options={tpl.data.filter((t) => (t.stage || "floor") === "floor").map((t) => ({ value: t.id, label: `${t.name} — цикл ${t.step_days} дн.` }))} />
          </Field>
          <Field label="Старт работ" hint={started ? "Работы уже идут — дату не изменить; сдвиньте план в «Графике цикла»" : "С первого этапа. Пока работы не начаты — план перестроится от новой даты"}>
            <Input type="date" value={cur.start_date} disabled={!!started} onChange={(e) => set({ start_date: e.target.value })} />
          </Field>
          <Field label="Смен в сутки"><Select value={cur.shifts_per_day} onChange={(e) => set({ shifts_per_day: e.target.value })}
            options={[{ value: "1", label: "1 — только день" }, { value: "2", label: "2 — день и ночь" }]} /></Field>
          <Field label="Код объекта в «Бетоне»"><Input value={cur.external_site_id} onChange={(e) => set({ external_site_id: e.target.value })} /></Field>
          <p className="muted small">Рабочие дни и праздники — кнопка «Календарь», этажи (добавить, тип, свой шаблон) — «Этажи», плановые объёмы — «Объёмы». Прежняя версия плана сохраняется в «Истории».</p>
        </div>
      )}
    </Drawer>
  );
}

function MembersDrawer({ project, users, onClose }: { project: any; users: any[]; onClose: () => void }) {
  const toast = useToast();
  const m = useAsync<any[]>(() => api.get(`/projects/${project.id}/members`), [project.id]);
  const [add, setAdd] = useState("");
  const set = async (user_id: string, building_ids: string[] | null) => {
    try { await api.put(`/projects/${project.id}/members`, { user_id, building_ids }); m.reload(); } catch (e) { toast.error(e); }
  };
  const membership = (u: any) => u.memberships.find((x: any) => x.project_id === project.id);
  return (
    <Drawer title={`Участники: ${project.name}`} onClose={onClose}>
      <div className="stack">
        {!m.data ? <Loading /> : m.data.length === 0 ? <p className="muted">Участников нет.</p> : m.data.map((u) => {
          const ms = membership(u);
          return (
            <div key={u.id} className="work-card">
              <div className="row between"><b>{u.full_name}</b><span className="muted small">{u.role || "роль не задана"} · {u.login}</span></div>
              <div className="row">
                <Button size="small" variant="ghost" onClick={async () => { try { await api.del(`/projects/${project.id}/members/${u.id}`); m.reload(); } catch (e) { toast.error(e); } }}>Убрать из проекта</Button>
              </div>
              {project.buildings.length > 1 && (
                <div className="row tight small">
                  <span className="muted">Корпуса:</span>
                  {project.buildings.map((b: any) => {
                    const ids: string[] = ms?.building_ids || [];
                    const on = ids.length === 0 || ids.includes(b.id);
                    return <label key={b.id} className="check"><input type="checkbox" checked={on} onChange={(e) => {
                      const all = project.buildings.map((x: any) => x.id);
                      const cur = ids.length === 0 ? all : ids;
                      const next = e.target.checked ? [...cur, b.id] : cur.filter((x: string) => x !== b.id);
                      if (!next.length) { toast.error("Оставьте хотя бы один корпус или уберите человека из проекта"); return; }
                      set(u.id, next.length === all.length ? null : next);
                    }} />{b.name}</label>;
                  })}
                </div>
              )}
            </div>
          );
        })}
        <div className="panel pad stack tight">
          <h3>Добавить участника</h3>
          <div className="row">
            <Select aria-label="Пользователь" className="grow" value={add} placeholder="Выберите пользователя" onChange={(e) => setAdd(e.target.value)}
              options={users.filter((u) => u.is_active && !u.is_admin && !m.data?.some((x) => x.id === u.id)).map((u) => ({ value: u.id, label: `${u.full_name}${u.role ? ` — ${u.role.toLowerCase()}` : ""}` }))} />
            <Button variant="primary" disabled={!add} onClick={() => { set(add, null); setAdd(""); }}>Добавить</Button>
          </div>
          <p className="muted small">Что человек может делать, задаёт его роль («Пользователи», «Роли и права»). Здесь — к какому проекту и каким корпусам у него доступ. Галочки корпусов не действуют для ролей с правом «видеть все корпуса своих проектов».</p>
        </div>
      </div>
    </Drawer>
  );
}

const SETTINGS: { key: string; label: string; hint: string }[] = [
  { key: "on_time_tolerance_pp", label: "Допуск «в срок», п. п.", hint: "Факт не ниже плана минус столько процентных пунктов" },
  { key: "task_lag_days", label: "Отставание работы, дн.", hint: "С какого отставания работа подсвечивается" },
  { key: "cycle_forecast_days", label: "Прогноз цикла позже, дн.", hint: "Предупреждение, если этаж закончится позже плана" },
  { key: "headcount_ratio", label: "Людей не меньше доли плана", hint: "0,8 — предупреждение при факте ниже 80% плана" },
  { key: "downtime_hours", label: "Простой, ч", hint: "Предупреждение, если простой за смену больше этого значения" },
  { key: "next_stage_days", label: "Риск следующей работы, дн.", hint: "За сколько дней предупреждать о задержке следующей работы" },
  { key: "day_shift_start", label: "Начало дневной смены, ч", hint: "Например, 8 — дневная смена с 8:00 до начала ночной" },
  { key: "night_shift_start", label: "Начало ночной смены, ч", hint: "Например, 20 — ночная смена с 20:00 до 8:00 следующего дня" },
  { key: "report_deadline_hour", label: "Отчёт смены до, ч", hint: "До какого часа следующего дня нужно сдать отчёт за смену (10 — до 10:00)" },
];

function SettingsDrawer({ project, onClose, onDone }: { project: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [v, setV] = useState<Record<string, any>>({ ...project.settings });
  return (
    <Drawer narrow title={`Пороги предупреждений: ${project.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" onClick={async () => {
        const out: Record<string, any> = { ...project.settings };
        for (const s of SETTINGS) out[s.key] = Number(String(v[s.key]).replace(",", ".").trim());
        const bad = SETTINGS.find((s) => String(v[s.key] ?? "").trim() === "" || !Number.isFinite(out[s.key]) || out[s.key] < 0);
        if (bad) { toast.error(`${bad.label}: введите число`); return; }
        out.require_volumes = !!v.require_volumes;
        try { await api.patch(`/projects/${project.id}`, { settings: out }); toast.ok("Пороги сохранены"); onDone(); } catch (e) { toast.error(e); }
      }}>Сохранить</Button>
    </>}>
      <div className="stack">
        {SETTINGS.map((s) => <Field key={s.key} label={s.label} hint={s.hint}><Input inputMode="decimal" value={v[s.key] ?? ""} onChange={(e) => setV({ ...v, [s.key]: e.target.value })} /></Field>)}
        <label className="check"><input type="checkbox" checked={!!v.require_volumes} onChange={(e) => setV({ ...v, require_volumes: e.target.checked })} /> Объём обязателен при 100% бетонирования и армирования</label>
      </div>
    </Drawer>
  );
}

/** Названия корпусов и проектов редактируемые (встреча 15.10, п. 10). */

// ------------------------------------------------------------------ календарь корпуса (ФТ-7.2)

const TIMEZONES = [
  ["Europe/Kaliningrad", "Калининград (МСК−1)"], ["Europe/Moscow", "Москва (МСК)"], ["Europe/Samara", "Самара (МСК+1)"],
  ["Asia/Yekaterinburg", "Екатеринбург (МСК+2)"], ["Asia/Omsk", "Омск (МСК+3)"], ["Asia/Novosibirsk", "Новосибирск (МСК+4)"],
  ["Asia/Krasnoyarsk", "Красноярск (МСК+4)"], ["Asia/Irkutsk", "Иркутск (МСК+5)"], ["Asia/Yakutsk", "Якутск (МСК+6)"],
  ["Asia/Vladivostok", "Владивосток (МСК+7)"], ["Asia/Magadan", "Магадан (МСК+8)"], ["Asia/Kamchatka", "Камчатка (МСК+9)"],
];

function CalendarDrawer({ building, onClose, onDone }: { building: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const d = useAsync<any>(() => api.get(`/buildings/${building.id}`), [building.id]);
  const [v, setV] = useState<any | null>(null);
  const [newDay, setNewDay] = useState("");
  const [busy, setBusy] = useState(false);
  const cur = v ?? (d.data ? {
    workdays: d.data.workdays, holidays: d.data.holidays || [], shifts_per_day: String(d.data.shifts_per_day),
    winter_from: d.data.winter_from || "", winter_to: d.data.winter_to || "", timezone: d.data.timezone || "Europe/Moscow",
  } : null);
  const set = (patch: any) => setV({ ...cur, ...patch });
  const fmt = (iso: string) => iso.split("-").reverse().join(".");
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/buildings/${building.id}`, {
        workdays: cur.workdays, holidays: cur.holidays, shifts_per_day: Number(cur.shifts_per_day),
        winter_from: cur.winter_from || null, winter_to: cur.winter_to || null, timezone: cur.timezone,
      });
      toast.ok("Календарь сохранён, план не начатых этажей пересчитан");
      onDone();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <Drawer narrow title={`Календарь: ${building.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!cur || busy || !cur.workdays.length} onClick={save}>Сохранить</Button>
    </>}>
      {!cur ? <Loading error={d.error} onRetry={d.reload} /> : (
        <div className="stack">
          <Field label="Рабочие дни" hint="Сроки работ считаются только по отмеченным дням">
            <div className="row tight">{WEEK.map((w, i) => (
              <label key={w} className="check"><input type="checkbox" checked={cur.workdays.includes(i)}
                onChange={(e) => set({ workdays: e.target.checked ? [...cur.workdays, i].sort() : cur.workdays.filter((x: number) => x !== i) })} />{w}</label>
            ))}</div>
          </Field>
          <Field label="Праздники и нерабочие дни" hint="В эти дни работы не планируются">
            <div className="stack tight">
              <div className="row tight">
                <Input type="date" value={newDay} onChange={(e) => setNewDay(e.target.value)} aria-label="Дата" />
                <Button size="small" disabled={!newDay || cur.holidays.includes(newDay)}
                  onClick={() => { set({ holidays: [...cur.holidays, newDay].sort() }); setNewDay(""); }}>Добавить</Button>
              </div>
              {cur.holidays.length === 0 ? <span className="muted small">Не заданы</span> : (
                <div className="row tight" style={{ flexWrap: "wrap" }}>{cur.holidays.map((h: string) => (
                  <span key={h} className="tag">{fmt(h)}
                    <button type="button" aria-label={`Убрать ${fmt(h)}`} style={{ marginLeft: 6, border: 0, background: "none", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
                      onClick={() => set({ holidays: cur.holidays.filter((x: string) => x !== h) })}>×</button>
                  </span>
                ))}</div>
              )}
            </div>
          </Field>
          <Field label="Смен в сутки"><Select value={cur.shifts_per_day} onChange={(e) => set({ shifts_per_day: e.target.value })}
            options={[{ value: "1", label: "1 — только день" }, { value: "2", label: "2 — день и ночь" }]} /></Field>
          <Field label="Зимний режим с" hint="Выдержки набора прочности — по зимним значениям шаблона">
            <Input type="date" value={cur.winter_from} onChange={(e) => set({ winter_from: e.target.value })} /></Field>
          <Field label="по"><Input type="date" value={cur.winter_to} onChange={(e) => set({ winter_to: e.target.value })} /></Field>
          <Field label="Часовой пояс корпуса"><Select value={cur.timezone} onChange={(e) => set({ timezone: e.target.value })}
            options={TIMEZONES.map(([value, label]) => ({ value, label }))} /></Field>
          <p className="muted small">Время начала дневной и ночной смены задаётся для всего проекта: «Пороги» у проекта.
            При изменении рабочих дней или праздников план не начатых этажей пересчитывается, прежняя версия сохраняется в «Истории».</p>
        </div>
      )}
    </Drawer>
  );
}

// ------------------------------------------------------------------ пользователи

function UsersTab() {
  const u = useAsync<any[]>(() => api.get("/users"), []);
  const p = useAsync<any[]>(() => api.get("/projects"), []);
  const [edit, setEdit] = useState<any | null>(null);
  const pname = (id: string) => p.data?.find((x) => x.id === id)?.name || "проект";
  if (!u.data) return <Loading error={u.error} onRetry={u.reload} />;
  return (
    <>
      <div><Button variant="primary" onClick={() => setEdit({})}>Новый пользователь</Button></div>
      <div className="panel table-wrap">
        <table className="table">
          <thead><tr><th>Имя</th><th>Логин</th><th>Телефон</th><th>Роль</th><th>Проекты</th><th>Статус</th></tr></thead>
          <tbody>{u.data.map((x) => (
            <tr key={x.id} className="click" onClick={() => setEdit(x)}>
              <td><b>{x.full_name}</b></td><td>{x.login}</td><td>{x.phone || "—"}</td>
              <td>{x.role || <span className="muted">не задана</span>}</td>
              <td className="small">{x.memberships.map((m: any) => pname(m.project_id)).join("; ") || <span className="muted">{x.is_admin ? "все" : "не назначен"}</span>}</td>
              <td><span className={`tag ${x.is_active ? "s-done" : "plain"}`}>{x.is_active ? "активен" : "отключён"}</span>{x.is_active && x.must_change_password && <div className="small muted">ещё не входил: пароль временный</div>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {edit && <UserDrawer u={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); u.reload(); }} />}
    </>
  );
}

/** Пароль, который легко продиктовать: без похожих букв и цифр (0/О, 1/l/I). */
export function makePassword(): string {
  const letters = "abcdefghjkmnpqrstuvwxyz", digits = "23456789";
  const rnd = (n: number) => { const a = new Uint32Array(1); crypto.getRandomValues(a); return a[0] % n; };
  const pick = (s: string) => s[rnd(s.length)];
  let out = "";
  for (let i = 0; i < 4; i++) out += pick(letters);
  for (let i = 0; i < 4; i++) out += pick(digits);
  return out[0].toUpperCase() + out.slice(1);
}

/** Поле пароля с кнопками «Показать» (замечание 24.10.2026, №8) и «Придумать» (встреча 24.10.2026). */
function PasswordInput({ value, onChange, autoFocus }: { value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="row tight" style={{ flexWrap: "nowrap" }}>
      <Input className="grow" type={shown ? "text" : "password"} autoComplete="new-password" autoFocus={autoFocus} value={value} onChange={(e) => onChange(e.target.value)} />
      <Button type="button" size="small" aria-pressed={shown} onClick={() => setShown(!shown)}>{shown ? "Скрыть" : "Показать"}</Button>
      <Button type="button" size="small" onClick={() => { onChange(makePassword()); setShown(true); }}>Придумать</Button>
    </div>
  );
}

/** Ссылка для входа с телефона: одноразовая, 7 дней; по ней человек входит и задаёт свой пароль. */
function InviteLink({ user }: { user: any }) {
  const toast = useToast();
  const [link, setLink] = useState<string | null>(null);
  const make = async () => {
    try {
      const r = await api.post(`/users/${user.id}/invite`, {});
      setLink(`${location.origin}${location.pathname}#/invite/${r.token}`);
    } catch (e) { toast.error(e); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link!); toast.ok("Ссылка скопирована — отправьте её в мессенджере"); }
    catch { toast.error("Не удалось скопировать — выделите ссылку и скопируйте вручную"); }
  };
  return (
    <div className="panel pad stack tight wide">
      <b>Вход с телефона</b>
      {link ? (
        <>
          <Input readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Ссылка для входа" />
          <div className="row tight"><Button size="small" variant="primary" onClick={copy}>Скопировать ссылку</Button><Button size="small" onClick={make}>Новая ссылка</Button></div>
          <span className="small muted">Отправьте ссылку человеку (например, в мессенджере). Он откроет её на телефоне, придумает свой пароль и поставит значок «Стройки» на главный экран. Ссылка одноразовая, действует 7 дней; новая ссылка отменяет прежнюю.</span>
        </>
      ) : (
        <>
          <span className="small muted">{user.invite_active ? "Ссылка уже выдана и ещё не использована. " : ""}Чтобы не диктовать логин и пароль — дайте ссылку: по ней вход без пароля, один раз.</span>
          <div><Button size="small" onClick={make}>{user.invite_active ? "Выдать новую ссылку" : "Ссылка для входа"}</Button></div>
        </>
      )}
    </div>
  );
}

function UserDrawer({ u, onClose, onDone }: { u: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const roles = useAsync<any[]>(() => api.get("/roles"), []);
  const isNew = !u.id;
  const [v, setV] = useState({ login: u.login || "", full_name: u.full_name || "", phone: u.phone || "", password: "", role_id: u.role_id || "", is_active: u.is_active ?? true });
  const role = roles.data?.find((r) => r.id === v.role_id);
  const missing = [!v.full_name.trim() && "имя", isNew && !v.login.trim() && "логин", isNew && v.password.length < 6 && "пароль (не меньше 6 символов)", !v.role_id && "роль"].filter(Boolean);
  const save = async () => {
    try {
      if (isNew) await api.post("/users", { login: v.login.trim(), full_name: v.full_name.trim(), phone: v.phone.trim() || null, password: v.password, role_id: v.role_id });
      else await api.patch(`/users/${u.id}`, { full_name: v.full_name.trim(), phone: v.phone.trim() || null, role_id: v.role_id, is_active: v.is_active, ...(v.password ? { password: v.password } : {}) });
      toast.ok(isNew ? "Пользователь создан. Добавьте его в проект: «Проекты и корпуса» → «Участники»" : "Сохранено");
      onDone();
    } catch (e) { toast.error(e); }
  };
  return (
    <Drawer narrow title={isNew ? "Новый пользователь" : u.full_name} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={missing.length > 0 || (!isNew && v.password !== "" && v.password.length < 6)} onClick={save}>{isNew ? "Создать" : "Сохранить"}</Button>
    </>}>
      <div className="form-grid">
        <Field label="Имя" wide><Input value={v.full_name} onChange={(e) => setV({ ...v, full_name: e.target.value })} placeholder="Фамилия Имя Отчество" /></Field>
        <Field label="Логин" hint={isNew ? "Латиницей, для входа" : "Логин не меняется"}><Input value={v.login} disabled={!isNew} onChange={(e) => setV({ ...v, login: e.target.value })} /></Field>
        <Field label="Телефон"><Input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} placeholder="+7 900 000-00-00" /></Field>
        <Field label="Роль в системе" wide hint={role ? role.permissions.length + " прав" + (role.is_admin ? " — все" : "") : "Что человек сможет делать; роли настраиваются на вкладке «Роли и права»"}>
          <Select value={v.role_id} placeholder="Выберите роль" onChange={(e) => setV({ ...v, role_id: e.target.value })}
            options={(roles.data || []).map((r) => ({ value: r.id, label: r.name }))} />
        </Field>
        <Field label={isNew ? "Пароль" : "Новый пароль"} hint={isNew ? "Временный: при первом входе человек придумает свой. Не меньше 6 символов" : "Пусто — пароль не меняется; новый пароль — тоже временный"} wide>
          <PasswordInput value={v.password} onChange={(password) => setV({ ...v, password })} />
        </Field>
        {!isNew && <label className="check wide"><input type="checkbox" checked={v.is_active} onChange={(e) => setV({ ...v, is_active: e.target.checked })} /> Может входить в систему</label>}
        {!isNew && u.is_active && <InviteLink user={u} />}
      </div>
      {missing.length > 0 && <p className="small muted">Заполните: {missing.join(", ")}.</p>}
    </Drawer>
  );
}

// ------------------------------------------------------------------ роли и права (замечание 24.10.2026, №2)

function RolesTab() {
  const r = useAsync<any[]>(() => api.get("/roles"), []);
  const perms = useAsync<any[]>(() => api.get("/permissions"), []);
  const [edit, setEdit] = useState<any | null>(null);
  if (!r.data || !perms.data) return <Loading error={r.error || perms.error} onRetry={r.reload} />;
  const label = (code: string) => perms.data!.find((p) => p.code === code)?.label || code;
  return (
    <>
      <div className="row between">
        <Button variant="primary" onClick={() => setEdit({ name: "", permissions: [] })}>Новая роль</Button>
        <span className="muted small">Роль одна на всю систему. Доступ к проектам и корпусам — в «Участниках» проекта.</span>
      </div>
      <div className="stack tight">{r.data.map((x) => (
        <div key={x.id} className="panel pad stack tight">
          <div className="row between">
            <div><b>{x.name}</b> <span className="muted small">· пользователей: {x.users}</span></div>
            {x.is_admin ? <span className="muted small">все права, не меняется</span> : <Button size="small" onClick={() => setEdit(x)}>Изменить</Button>}
          </div>
          <div className="small">{x.permissions.length ? x.permissions.map(label).join("; ") : <span className="muted">прав нет — только просмотр своих корпусов</span>}</div>
        </div>
      ))}</div>
      {edit && <RoleDrawer role={edit} perms={perms.data} onClose={() => setEdit(null)} onDone={() => { setEdit(null); r.reload(); }} />}
    </>
  );
}

function RoleDrawer({ role, perms, onClose, onDone }: { role: any; perms: { code: string; label: string; group: string }[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const isNew = !role.id;
  const [name, setName] = useState<string>(role.name);
  const [sel, setSel] = useState<string[]>(role.permissions);
  const [busy, setBusy] = useState(false);
  const groups = [...new Set(perms.map((p) => p.group))];
  const toggle = (code: string, on: boolean) => setSel(on ? [...sel, code] : sel.filter((x) => x !== code));
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: name.trim(), permissions: sel };
      if (isNew) await api.post("/roles", body); else await api.put(`/roles/${role.id}`, body);
      toast.ok(isNew ? "Роль создана" : "Роль сохранена. Люди с этой ролью войдут заново, чтобы права обновились");
      onDone();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.del(`/roles/${role.id}`); toast.ok("Роль удалена"); onDone(); } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <Drawer narrow title={isNew ? "Новая роль" : `Роль: ${role.name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      {!isNew && <Button variant="ghost" disabled={busy} onClick={remove}>Удалить роль</Button>}
      <Button variant="primary" disabled={busy || name.trim().length < 2} onClick={save}>{isNew ? "Создать" : "Сохранить"}</Button>
    </>}>
      <div className="stack">
        <Field label="Название роли"><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Начальник участка" /></Field>
        {groups.map((g) => (
          <div key={g} className="stack tight">
            <h3>{g}</h3>
            {perms.filter((p) => p.group === g).map((p) => (
              <label key={p.code} className="check"><input type="checkbox" checked={sel.includes(p.code)} onChange={(e) => toggle(p.code, e.target.checked)} /> {p.label}</label>
            ))}
          </div>
        ))}
        <p className="muted small">Без галочек человек видит свои корпуса (шахматку, график, фото, историю) и может завести проблему. Удалить можно только роль, которая никому не назначена.</p>
      </div>
    </Drawer>
  );
}

// ------------------------------------------------------------------ справочники (замечание 24.10.2026, №5)

const KIND_RU: Record<string, string> = {
  rebar: "Армирование", form: "Монтаж опалубки", form_concrete: "Опалубка и бетонирование", concrete: "Бетонирование",
  strip: "Демонтаж опалубки", other: "Прочее",
};
const GROUP_RU: Record<string, string> = { vertical: "стены и колонны", slab: "перекрытие", general: "участок этапа" };

function CatalogsTab() {
  return (
    <div className="stack">
      <LegalEntities />
      <WorkTypes />
      <CrewsTable />
      <NamedList title="Причины простоя" kind="downtime-reasons" hint="Выбираются в отчёте прораба и видны в «Узких местах»." />
      <NamedList title="Категории проблем" kind="issue-categories" hint="Выбираются в карточке проблемы." />
    </div>
  );
}

export const LEGAL_ROLE: Record<string, string> = { contractor: "подрядчик", developer: "застройщик", general: "генподрядчик", sub: "субподрядчик" };

/** Справочник юрлиц с контактами (встреча 24.10.2026). */
function LegalEntities() {
  const l = useAsync<any[]>(() => api.get("/legal-entities"), []);
  const [edit, setEdit] = useState<any | null>(null);
  return (
    <div className="panel">
      <div className="ph"><h2 className="grow">Юрлица</h2><Button size="small" variant="primary" onClick={() => setEdit({})}>Новое юрлицо</Button></div>
      {!l.data ? <Loading error={l.error} onRetry={l.reload} /> : l.data.length === 0 ? <div className="pb muted small">Юрлиц пока нет.</div> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Название</th><th>ИНН</th><th>Контакт</th><th>Телефон, почта</th><th className="num">Проектов</th></tr></thead>
            <tbody>{l.data.map((x) => (
              <tr key={x.id} className="click" onClick={() => setEdit(x)}>
                <td><b>{x.name}</b>{x.address ? <div className="small muted">{x.address}</div> : null}</td><td>{x.inn || "—"}</td><td>{x.contact_person || "—"}</td>
                <td className="small">{[x.phone, x.email].filter(Boolean).join(", ") || "—"}</td><td className="num">{x.projects}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <div className="pb small muted">Юрлицо выбирается в проекте вместе с ролью: подрядчик, застройщик, генподрядчик или субподрядчик.</div>
      {edit && <LegalEntityDrawer e={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); l.reload(); }} />}
    </div>
  );
}

function LegalEntityDrawer({ e, onClose, onDone }: { e: any; onClose: () => void; onDone: (x?: any) => void }) {
  const toast = useToast();
  const isNew = !e.id;
  const F = ["name", "inn", "kpp", "address", "contact_person", "phone", "email", "note"] as const;
  const [v, setV] = useState<Record<string, string>>(Object.fromEntries(F.map((k) => [k, e[k] || ""])));
  const set = (k: string, x: string) => setV({ ...v, [k]: x });
  const save = async () => {
    try {
      const body = Object.fromEntries(F.map((k) => [k, v[k].trim() || null]));
      const r = isNew ? await api.post("/legal-entities", body) : await api.put(`/legal-entities/${e.id}`, body);
      toast.ok(isNew ? "Юрлицо добавлено" : "Сохранено");
      onDone(r);
    } catch (err) { toast.error(err); }
  };
  return (
    <Drawer narrow title={isNew ? "Новое юрлицо" : e.name} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      {!isNew && <Button variant="ghost" onClick={async () => { try { await api.del(`/legal-entities/${e.id}`); toast.ok("Юрлицо убрано из справочника"); onDone(); } catch (err) { toast.error(err); } }}>Удалить</Button>}
      <Button variant="primary" disabled={v.name.trim().length < 2} onClick={save}>{isNew ? "Добавить" : "Сохранить"}</Button>
    </>}>
      <div className="form-grid">
        <Field label="Название" wide><Input autoFocus value={v.name} onChange={(x) => set("name", x.target.value)} placeholder="Например: ООО «Монолит-Строй»" /></Field>
        <Field label="ИНН" hint="10 цифр, у ИП — 12"><Input inputMode="numeric" value={v.inn} onChange={(x) => set("inn", x.target.value.replace(/\D/g, ""))} /></Field>
        <Field label="КПП" hint="Необязательно"><Input inputMode="numeric" value={v.kpp} onChange={(x) => set("kpp", x.target.value.replace(/\D/g, ""))} /></Field>
        <Field label="Адрес" wide><Input value={v.address} onChange={(x) => set("address", x.target.value)} /></Field>
        <Field label="Контактное лицо" wide><Input value={v.contact_person} onChange={(x) => set("contact_person", x.target.value)} placeholder="Фамилия Имя, должность" /></Field>
        <Field label="Телефон"><Input value={v.phone} onChange={(x) => set("phone", x.target.value)} placeholder="+7 900 000-00-00" /></Field>
        <Field label="Почта"><Input value={v.email} onChange={(x) => set("email", x.target.value)} /></Field>
        <Field label="Примечание" wide><Input value={v.note} onChange={(x) => set("note", x.target.value)} /></Field>
      </div>
    </Drawer>
  );
}

function WorkTypes() {
  const w = useAsync<any[]>(() => api.get("/work-types"), []);
  const [edit, setEdit] = useState<any | null>(null);
  return (
    <div className="panel">
      <div className="ph"><h2 className="grow">Виды работ</h2><Button size="small" variant="primary" onClick={() => setEdit({})}>Новая работа</Button></div>
      {!w.data ? <Loading error={w.error} onRetry={w.reload} /> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Работа</th><th>Полное название</th><th>Где</th><th>Что это</th><th>Ед.</th><th>Бригада</th></tr></thead>
            <tbody>{w.data.map((x) => (
              <tr key={x.id} className="click" onClick={() => setEdit(x)}>
                <td><b>{x.short_name}</b></td><td className="small">{x.name}</td><td>{GROUP_RU[x.group]}</td><td>{KIND_RU[x.kind] || "—"}</td><td>{x.unit || "—"}</td><td>{x.crew || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <div className="pb small muted">Новая работа появится в шаблонах циклов («Добавить строку»). «Что это» определяет, в какой столбец «Производства» она попадёт и нужен ли объём при 100%.</div>
      {edit && <WorkTypeDrawer w={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); w.reload(); }} />}
    </div>
  );
}

function WorkTypeDrawer({ w, onClose, onDone }: { w: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const crews = useAsync<any[]>(() => api.get("/crews"), []);
  const isNew = !w.id;
  const [v, setV] = useState({ short_name: w.short_name || "", name: w.name || "", group: w.group || "", kind: w.kind || "", unit: w.unit || "", default_crew_code: w.crew || "" });
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.ok(ok); onDone(); } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const body = { ...v, short_name: v.short_name.trim(), name: (v.name.trim() || v.short_name.trim()), unit: v.unit.trim() || null, default_crew_code: v.default_crew_code || null };
  return (
    <Drawer narrow title={isNew ? "Новый вид работ" : `Работа: ${w.short_name}`} onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      {!isNew && <Button variant="ghost" disabled={busy} onClick={() => run(() => api.del(`/work-types/${w.id}`), "Работа убрана из справочника")}>Удалить</Button>}
      <Button variant="primary" disabled={busy || v.short_name.trim().length < 2 || !v.group || !v.kind}
        onClick={() => run(() => (isNew ? api.post("/work-types", body) : api.put(`/work-types/${w.id}`, body)), isNew ? "Работа добавлена" : "Сохранено")}>{isNew ? "Добавить" : "Сохранить"}</Button>
    </>}>
      <div className="form-grid">
        <Field label="Короткое название" wide hint="Так работа видна в сетке, отчёте и шахматке"><Input value={v.short_name} onChange={(e) => setV({ ...v, short_name: e.target.value })} placeholder="Например: Гидроизоляция" /></Field>
        <Field label="Полное название" wide hint="Необязательно"><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Например: Устройство гидроизоляции фундаментной плиты" /></Field>
        <Field label="Где" hint={!isNew ? "Меняется, пока работы нет в шаблонах" : undefined}><Select value={v.group} placeholder="Выберите" onChange={(e) => setV({ ...v, group: e.target.value })}
          options={[{ value: "vertical", label: "Стены и колонны (захватки С)" }, { value: "slab", label: "Перекрытие, фундаментная плита (захватки П)" }, { value: "general", label: "Этап целиком: котлован, подбетонка… (участки У)" }]} /></Field>
        <Field label="Что это за работа"><Select value={v.kind} placeholder="Выберите" disabled={!isNew} onChange={(e) => setV({ ...v, kind: e.target.value })}
          options={Object.entries(KIND_RU).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="Единица объёма" hint="т, м², м³; пусто — по виду работы"><Input value={v.unit} onChange={(e) => setV({ ...v, unit: e.target.value })} /></Field>
        <Field label="Специальность по умолчанию"><Select value={v.default_crew_code} placeholder="—" onChange={(e) => setV({ ...v, default_crew_code: e.target.value })}
          options={(crews.data || []).map((c) => ({ value: c.code, label: `${c.code} · ${c.name}` }))} /></Field>
      </div>
    </Drawer>
  );
}

function CrewsTable() {
  const toast = useToast();
  const c = useAsync<any[]>(() => api.get("/crews"), []);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [add, setAdd] = useState({ code: "", name: "", planned_headcount: "", foreman_name: "" });
  if (!c.data) return <div className="panel"><Loading error={c.error} onRetry={c.reload} /></div>;
  const save = async (crew: any) => {
    const d = draft[crew.id];
    if (d.planned_headcount !== undefined && !/^\d{1,3}$/.test(String(d.planned_headcount).trim())) {
      toast.error("Людей на смену: введите целое число, например 9");
      return;
    }
    if (d.name !== undefined && !String(d.name).trim()) { toast.error("Название специальности: заполните поле"); return; }
    try {
      await api.patch(`/crews/${crew.id}`, { ...d, planned_headcount: d.planned_headcount !== undefined ? Number(d.planned_headcount) : undefined });
      toast.ok("Специальность сохранена");
      setDraft((x) => { const n = { ...x }; delete n[crew.id]; return n; });
      c.reload();
    } catch (e) { toast.error(e); }
  };
  const create = async () => {
    if (!/^\d{1,3}$/.test(add.planned_headcount.trim() || "0")) { toast.error("Людей на смену: введите целое число, например 9"); return; }
    try {
      await api.post("/crews", { code: add.code.trim(), name: add.name.trim(), planned_headcount: Number(add.planned_headcount || 0), foreman_name: add.foreman_name.trim() || null });
      toast.ok("Специальность добавлена"); setAdd({ code: "", name: "", planned_headcount: "", foreman_name: "" }); c.reload();
    } catch (e) { toast.error(e); }
  };
  return (
    <div className="panel">
      <div className="ph"><h2>Специальности</h2></div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Код</th><th>Название</th><th className="num">Людей на смену по плану</th><th /></tr></thead>
          <tbody>{c.data.map((x) => {
            const d = { ...x, ...draft[x.id] };
            const ch = (p: any) => setDraft({ ...draft, [x.id]: { ...draft[x.id], ...p } });
            return (
              <tr key={x.id}>
                <td><b>{x.code}</b></td>
                <td><Input aria-label="Название" value={d.name} onChange={(e) => ch({ name: e.target.value })} /></td>
                <td className="num"><Input aria-label="Людей" inputMode="numeric" style={{ width: 80 }} value={d.planned_headcount ?? ""} onChange={(e) => ch({ planned_headcount: e.target.value })} /></td>
                <td className="row tight">
                  {draft[x.id] && <Button size="small" variant="primary" onClick={() => save(x)}>Сохранить</Button>}
                  <Button size="small" variant="ghost" onClick={async () => { try { await api.del(`/crews/${x.id}`); toast.ok("Специальность убрана"); c.reload(); } catch (e) { toast.error(e); } }}>Убрать</Button>
                </td>
              </tr>
            );
          })}
            <tr>
              <td><Input aria-label="Код новой специальности" style={{ width: 60 }} placeholder="С" value={add.code} onChange={(e) => setAdd({ ...add, code: e.target.value })} /></td>
              <td><Input aria-label="Название новой специальности" placeholder="Например: Сварщики" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} /></td>
              <td className="num"><Input aria-label="Людей новой специальности" inputMode="numeric" style={{ width: 80 }} value={add.planned_headcount} onChange={(e) => setAdd({ ...add, planned_headcount: e.target.value })} /></td>
              <td><Button size="small" disabled={!add.code.trim() || add.name.trim().length < 2} onClick={create}>Добавить</Button></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="pb small muted">Специальности — арматурщики, монтажники, плотники, сварщики, электрики (прогрев)… Код (одна-две буквы) виден в сетке шаблона и в отчёте; людей на смену по плану — для предупреждений «мало людей». Бригады с бригадирами заводятся у корпуса: «Бригады» → «Бригады и бригадиры».</div>
    </div>
  );
}

function NamedList({ title, kind, hint }: { title: string; kind: "downtime-reasons" | "issue-categories"; hint: string }) {
  const toast = useToast();
  const l = useAsync<any[]>(() => api.get(`/${kind}`), [kind]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [name, setName] = useState("");
  const run = async (fn: () => Promise<any>, ok: string) => { try { await fn(); toast.ok(ok); l.reload(); } catch (e) { toast.error(e); } };
  return (
    <div className="panel">
      <div className="ph"><h2>{title}</h2></div>
      {!l.data ? <Loading error={l.error} onRetry={l.reload} /> : (
        <div className="pb stack tight">
          {l.data.map((x) => (
            <div key={x.id} className="row" style={{ flexWrap: "nowrap" }}>
              <Input aria-label={`${title}: ${x.name}`} className="grow" value={draft[x.id] ?? x.name} onChange={(e) => setDraft({ ...draft, [x.id]: e.target.value })} />
              {draft[x.id] !== undefined && draft[x.id] !== x.name && <Button size="small" variant="primary" disabled={draft[x.id].trim().length < 2}
                onClick={() => run(() => api.patch(`/catalog/${kind}/${x.id}`, { name: draft[x.id].trim() }), "Сохранено").then(() => setDraft((d) => { const n = { ...d }; delete n[x.id]; return n; }))}>Сохранить</Button>}
              <Button size="small" variant="ghost" onClick={() => run(() => api.del(`/catalog/${kind}/${x.id}`), "Убрано из списка")}>Убрать</Button>
            </div>
          ))}
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <Input aria-label={`Новое значение: ${title}`} className="grow" placeholder="Новое значение" value={name} onChange={(e) => setName(e.target.value)} />
            <Button size="small" disabled={name.trim().length < 2} onClick={() => run(() => api.post(`/catalog/${kind}`, { name: name.trim() }), "Добавлено").then(() => setName(""))}>Добавить</Button>
          </div>
          <div className="small muted">{hint} Убранное значение остаётся в прошлых записях, но в новых его не выбрать.</div>
        </div>
      )}
    </div>
  );
}

type Vol = { grips: string[]; kinds: { key: string; label: string }[]; floors: { number: number; kind: string; grips: Record<string, Record<string, number>>; totals: Record<string, number | null> }[] };

function VolumesDrawer({ building, onClose }: { building: any; onClose: () => void }) {
  const toast = useToast();
  const d = useAsync<Vol>(() => api.get(`/buildings/${building.id}/volumes`), [building.id]);
  const [kind, setKind] = useState("concrete_m3");
  const [draft, setDraft] = useState<Record<string, string>>({}); // «этаж|захватка|вид» → введённый текст
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  if (!d.data) return <Drawer title={`Объёмы: ${building.name}`} onClose={onClose}><Loading error={d.error} onRetry={d.reload} /></Drawer>;
  const data = d.data;
  const k = (f: number, g: string, kk = kind) => `${f}|${g}|${kk}`;
  const val = (f: number, g: string, kk = kind): string => {
    const key = k(f, g, kk);
    if (key in draft) return draft[key];
    const v = data.floors.find((x) => x.number === f)?.grips[g]?.[kk];
    return v == null ? "" : String(v).replace(".", ",");
  };
  const num = (t: string) => (t.trim() === "" ? null : Number(t.replace(",", ".").replace(/\s/g, "")));
  const total = (f: number) => {
    const xs = data.grips.map((g) => num(val(f, g))).filter((x): x is number => x != null && !Number.isNaN(x));
    return xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) * 1000) / 1000 : null;
  };
  const dirty = Object.keys(draft).length > 0;
  const bad = Object.values(draft).some((t) => { const n = num(t); return n != null && (Number.isNaN(n) || n < 0); });
  const copyFloor = () => {
    const src = Number(from);
    if (!data.floors.some((x) => x.number === src)) return;
    const next = { ...draft };
    for (const f of data.floors) {
      if (f.number === src || f.kind === "underground") continue;
      for (const g of data.grips) for (const kk of data.kinds.map((x) => x.key)) next[k(f.number, g, kk)] = val(src, g, kk);
    }
    setDraft(next);
    toast.ok(`Объёмы этажа ${src} скопированы на остальные этажи — проверьте и сохраните`);
  };
  const save = async () => {
    setBusy(true);
    try {
      const floors = data.floors
        .filter((f) => data.grips.some((g) => data.kinds.some((x) => k(f.number, g, x.key) in draft)))
        .map((f) => ({
          number: f.number,
          grips: Object.fromEntries(data.grips.map((g) => [g, Object.fromEntries(data.kinds.map((x) => [x.key, num(val(f.number, g, x.key))]))])),
        }));
      const r = await api.put(`/buildings/${building.id}/volumes`, { floors });
      d.set(r);
      setDraft({});
      toast.ok(`Объёмы сохранены: этажей ${r.floors_changed}, плановый объём обновлён у работ: ${r.tasks_changed}`);
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/buildings/${building.id}/volumes/import`, fd);
      d.set(r);
      setDraft({});
      toast.ok(`Загружено из Excel: этажей ${r.floors_changed}, работ обновлено ${r.tasks_changed}`);
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const unit = data.kinds.find((x) => x.key === kind)?.label || "";
  return (
    <Drawer title={`Плановые объёмы: ${building.name}`} onClose={onClose} footer={<>
      <Button variant="primary" disabled={!dirty || bad || busy} onClick={save}>Сохранить</Button>
      <Button disabled={!dirty || busy} onClick={() => setDraft({})}>Отменить правки</Button>
      <span className="grow" />
      <Button disabled={busy} onClick={() => api.open(`/buildings/${building.id}/volumes/export.xlsx`, undefined, `Объёмы — ${building.name}.xlsx`).catch(toast.error)}>Выгрузить в Excel</Button>
      <label className={`btn${busy ? " disabled" : ""}`}>Загрузить из Excel
        <input type="file" accept=".xlsx" hidden onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
    </>}>
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          Объём захватки становится планом её работ: бетон — для бетонирования, арматура — для армирования, опалубка — для монтажа и демонтажа опалубки.
          Итог этажа — сумма захваток. В Excel: столбцы «Этаж, Захватка, Бетон, Арматура, Опалубка»; в «Этаж» можно писать номер, диапазон «2-17» или «все».
        </p>
        <Tabs value={kind} onChange={setKind} items={data.kinds.map((x) => ({ value: x.key, label: x.label }))} />
        <div className="row tight">
          <span className="small">Скопировать все объёмы этажа</span>
          <Select aria-label="Этаж-образец" style={{ width: 90 }} value={from} onChange={(e) => setFrom(e.target.value)} placeholder="—"
            options={data.floors.map((f) => ({ value: String(f.number), label: String(f.number) }))} />
          <Button size="small" disabled={!from} onClick={copyFloor}>на все этажи</Button>
        </div>
        {bad && <div className="alert risk"><div className="t">Объём должен быть числом не меньше нуля — исправьте ячейки, выделенные красным.</div></div>}
        <div className="table-wrap">
          <table className="table vol-table">
            <thead><tr><th className="num">Этаж</th>{data.grips.map((g) => <th key={g} className="num">{g}</th>)}<th className="num">Итого, {unit.split(", ")[1]}</th></tr></thead>
            <tbody>{[...data.floors].reverse().map((f) => (
              <tr key={f.number}>
                <td className="num"><b>{f.number}</b></td>
                {data.grips.map((g) => {
                  const t = val(f.number, g);
                  const n = num(t);
                  const err = n != null && (Number.isNaN(n) || n < 0);
                  return (
                    <td key={g} className="num">
                      <input className="input" inputMode="decimal" aria-label={`Этаж ${f.number}, ${g}, ${unit}`} value={t}
                        style={err ? { borderColor: "var(--st-conflict)" } : k(f.number, g) in draft ? { background: "var(--st-planned-bg)" } : undefined}
                        onChange={(e) => setDraft({ ...draft, [k(f.number, g)]: e.target.value })} />
                    </td>
                  );
                })}
                <td className="num"><b>{total(f.number)?.toLocaleString("ru-RU") ?? "—"}</b></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </Drawer>
  );
}
